/**
 * Walls — the modular wall kit.
 * A wall is a punched panel (every reveal bevelled by the extruder) plus the
 * trim that makes it read as architecture: plinth, string course, cornice,
 * pilasters, sills, lintels, parapet coping, and plaster blown off the brick.
 *
 * owner: level
 */
import * as THREE from 'three';
import {
  chamferBox, wallPanel, corniceProfile, extrudeShape, xform, quadXY,
} from '../Geo.js';

/**
 * Plaster blown off a wall, revealing brick.
 *
 * Built from many small overlapping slabs, each ROLLED about the wall normal.
 * The roll is the whole trick: axis-aligned rectangles, however many you stack,
 * produce a staircase silhouette that reads as a pasted decal — which is
 * exactly how the first version looked in `level_detail`. Rolling each slab a
 * few degrees breaks every horizontal, and the brick is tinted down and cooled
 * per slab because raw brick_wall is a saturated red that screams "library
 * texture" against sun-bleached plaster; real exposed brick is dust-covered.
 */
/*
 * The exposed core is rendered BLOCKWORK, not fired brick.
 *
 * Two reasons. Materially, this is a Levantine coastal town: walls are concrete
 * block under a lime render, and red English-bond brick reads as a different
 * continent. Technically, a vertex tint can only scale channels, so it cannot
 * desaturate `brick_wall`'s saturated red — every attempt just produced a
 * darker red, which is exactly how the first two passes of `level_detail`
 * looked. Swapping the material solves what a tint provably cannot.
 */
const CORE_MAT = 'concrete_wall';
// Distinctly darker than the render around it. The core is at the bottom of a
// 4 cm recess with no sky access, so if it sits at the same value as the
// plaster the patch reads as a stack of tiles pasted on rather than a hole.
const CORE_DUST = [
  [0.58, 0.53, 0.46],
  [0.46, 0.43, 0.40],
  [0.66, 0.59, 0.50],
  [0.41, 0.39, 0.37],
  [0.61, 0.55, 0.47],
];
export function damagePatch(w, { cx, cy, cz, rotY = 0, w: pw = 1.4, h: ph = 1.0, plasterMat = 'plaster_wall', plasterSurface = 'concrete', nrm = 1, rng }) {
  const r = rng || w.rng;
  const cos = Math.cos(rotY), sin = Math.sin(rotY);
  // `nrm` is which way is OUT of the wall along local +normal. Without it the
  // recess/proud offsets below flip sign on back-face patches and the core ends
  // up floating in front of the render instead of behind it.
  const at = (u, v) => [cx + cos * u + sin * (v * nrm), cz - sin * u + cos * (v * nrm)];
  // exposed brick core: many small slabs, rolled, so the outline is ragged
  const n = 6 + Math.floor(r() * 4);
  for (let i = 0; i < n; i++) {
    const bw = pw * r.range(0.16, 0.62), bh = ph * r.range(0.16, 0.62);
    const ox = r.range(-1, 1) * (pw - bw) * 0.5;
    const oy = r.range(-1, 1) * (ph - bh) * 0.5;
    // recessed: the plaster blew OFF, so the core sits behind the render face
    const p = at(ox, r.range(-0.048, -0.028));
    const g = chamferBox(bw, bh, 0.055, 0.018);
    const d = CORE_DUST[Math.floor(r() * CORE_DUST.length) % CORE_DUST.length];
    const k = r.range(0.86, 1.14);
    w.batch.add(g, CORE_MAT, {
      // [pitch, yaw, roll] with 'YXZ' order — roll is in the wall's own plane
      matrix: xform([p[0], cy + oy, p[1]], [0, rotY + r.range(-0.03, 0.03), r.range(-0.30, 0.30)]),
      surface: 'concrete', uvScale: 0.55, tint: [d[0] * k, d[1] * k, d[2] * k],
    });
    g.dispose();
  }
  // torn plaster edge — fragments still keyed to the wall around the hole
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + r.range(-0.5, 0.5);
    const fw = pw * r.range(0.10, 0.24), fh = ph * r.range(0.10, 0.24);
    // ringing the rim, not sitting over the hole
    const ox = Math.cos(a) * pw * r.range(0.44, 0.56);
    const oy = Math.sin(a) * ph * r.range(0.44, 0.56);
    const p = at(ox, 0.012);
    // thin, so the fragment reads as spalled render rather than a floating tile
    const g = chamferBox(fw, fh, 0.03, 0.012);
    w.batch.add(g, plasterMat, {
      matrix: xform([p[0], cy + oy, p[1]], [0, rotY + r.range(-0.04, 0.04), r.range(-0.35, 0.35)]),
      surface: plasterSurface, uvScale: 0.8, tint: [0.93, 0.91, 0.87],
    });
    g.dispose();
  }
}

/** Wall-local frame: u = along the wall from its centre, o = outward (local +Z). */
export function wallFrame(a, b) {
  const dx = b[0] - a[0], dz = b[1] - a[1];
  const L = Math.hypot(dx, dz) || 1e-4;
  const dir = [dx / L, dz / L];
  const nrm = [-dz / L, dx / L];
  const rotY = Math.atan2(-dz, dx);
  const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  return {
    L, dir, nrm, rotY, mid,
    at(u, o = 0) { return [mid[0] + dir[0] * u + nrm[0] * o, mid[1] + dir[1] * u + nrm[1] * o]; },
  };
}

const DEF_OPEN = { x: 0, y: 0.9, w: 1.1, h: 1.5, arch: false, sill: true, lintel: true };

export function wall(w, spec) {
  const {
    a, b, base = 0, height = 4.2, thickness = 0.4,
    mat = 'plaster_wall', surface = 'concrete',
    openings = [], bevel = 0.045, uvScale = 1.7,
    plinth = true, cornice = null, band = null, parapet = null,
    pilasters = null, collide = true, damage = 0, trimMat = 'stucco', reveal = false,
    tint = null, cast = true,
  } = spec;

  const f = wallFrame(a, b);
  const rng = w.rng;
  const ops = openings.map((o) => ({ ...DEF_OPEN, ...o }));

  /* --- main punched panel ------------------------------------------- */
  const panel = wallPanel({ length: f.L, height, thickness, openings: ops, bevel });
  w.batch.add(panel, mat, {
    matrix: xform([f.mid[0], base, f.mid[1]], f.rotY),
    surface, uvScale, tint, cast,
  });
  panel.dispose();

  /* --- plinth / base course ----------------------------------------- */
  if (plinth) {
    const p = typeof plinth === 'object' ? plinth : {};
    const ph = p.h ?? 0.42, proj = p.proj ?? 0.07;
    const g = chamferBox(f.L, ph, thickness + proj * 2, 0.045);
    w.batch.add(g, p.mat ?? 'concrete_wall', {
      matrix: xform([f.mid[0], base + ph / 2, f.mid[1]], f.rotY),
      surface: 'concrete', uvScale: 1.2,
    });
    g.dispose();
  }

  /* --- string course / floor band ----------------------------------- */
  if (band) {
    const bh = band.h ?? 0.16, proj = band.proj ?? 0.09;
    const g = chamferBox(f.L + 0.04, bh, thickness + proj * 2, 0.038);
    w.batch.add(g, band.mat ?? trimMat, {
      matrix: xform([f.mid[0], base + band.y, f.mid[1]], f.rotY),
      surface: 'concrete', uvScale: 1.0,
    });
    g.dispose();
  }

  /* --- sills, lintels, glass, grilles -------------------------------- */
  for (const o of ops) {
    if (o.sill && o.y > 0.25) {
      const g = chamferBox(o.w + 0.30, 0.11, thickness + 0.22, 0.032);
      const p = f.at(o.x);
      w.batch.add(g, 'concrete_wall', {
        matrix: xform([p[0], base + o.y - 0.05, p[1]], f.rotY), surface: 'concrete', uvScale: 0.8,
      });
      g.dispose();
    }
    if (o.lintel && !o.arch) {
      const g = chamferBox(o.w + 0.42, 0.21, thickness + 0.10, 0.034);
      const p = f.at(o.x);
      w.batch.add(g, 'concrete_wall', {
        matrix: xform([p[0], base + o.y + o.h + 0.10, p[1]], f.rotY), surface: 'concrete', uvScale: 0.8,
      });
      g.dispose();
    }
    if (o.arch) {
      // voussoir band tracing the arch head
      const r = o.w / 2;
      const s = new THREE.Shape();
      const ro = r + 0.22;
      s.absarc(0, 0, ro, Math.PI, 0, true);
      s.lineTo(r, 0);
      s.absarc(0, 0, r, 0, Math.PI, false);
      s.closePath();
      const g = extrudeShape(s, thickness + 0.08, 0.03, 16);
      const p = f.at(o.x);
      w.batch.add(g, 'concrete_wall', {
        matrix: xform([p[0], base + o.y + o.h - r, p[1]], f.rotY), surface: 'concrete', uvScale: 0.9,
      });
      g.dispose();
    }
    if (o.glass) {
      /*
       * Reveal depth.
       *
       * `level_alley` (600–660, 425–640) and (1300–1400, 400–620) read as "black
       * rectangles with painted mullions — no glass, no reveal depth, no sill
       * shadow". The pane was sitting 6 cm inside the outer face of a 36–42 cm
       * wall, so from any oblique angle there was nothing between the render and
       * the glass and the window was a flat decal. It now sits 14 cm in, behind a
       * returned lining that gives the head a soffit and the jambs a shadow —
       * one bevelled extruded ring, so it is one merged piece per window.
       */
      const inset = Math.max(-thickness / 2 + 0.05, thickness / 2 - 0.14);
      const p = f.at(o.x, inset);
      if (reveal && !o.arch) {
        const rw = o.w / 2, rh = o.h / 2, lip = 0.065;
        const ring = new THREE.Shape();
        ring.moveTo(-rw, -rh); ring.lineTo(rw, -rh); ring.lineTo(rw, rh); ring.lineTo(-rw, rh); ring.closePath();
        const hole = new THREE.Path();
        hole.moveTo(-rw + lip, -rh + lip); hole.lineTo(rw - lip, -rh + lip);
        hole.lineTo(rw - lip, rh - lip); hole.lineTo(-rw + lip, rh - lip); hole.closePath();
        ring.holes.push(hole);
        const g2 = extrudeShape(ring, Math.max(0.10, thickness / 2 - inset + 0.02), 0.022, 2);
        const pr = f.at(o.x, (thickness / 2 + inset) / 2);
        w.batch.add(g2, 'concrete_wall', {
          matrix: xform([pr[0], base + o.y + o.h / 2, pr[1]], f.rotY),
          surface: 'concrete', uvScale: 0.6, cast: false, tint: [0.74, 0.72, 0.69],
        });
        g2.dispose();
      }
      // two-sided *geometry* (not material.side, which we do not own) so the
      // pane still reads from inside the room looking out
      const g = quadXY(o.w - 0.09, o.h - 0.09, true);
      w.batch.add(g, o.glass === 'dirty' ? 'glass_dirty' : 'glass', {
        matrix: xform([p[0], base + o.y + o.h / 2, p[1]], f.rotY),
        surface: 'glass', uvScale: 1, cast: false,
      });
      g.dispose();
      // mullion cross
      const mv = chamferBox(0.065, o.h - 0.09, 0.055, 0.016);
      w.batch.add(mv, 'metal_painted', {
        matrix: xform([p[0], base + o.y + o.h / 2, p[1]], f.rotY), surface: 'metal', uvScale: 0.5,
      });
      mv.dispose();
      const mh = chamferBox(o.w - 0.09, 0.065, 0.055, 0.016);
      w.batch.add(mh, 'metal_painted', {
        matrix: xform([p[0], base + o.y + o.h * 0.52, p[1]], f.rotY), surface: 'metal', uvScale: 0.5,
      });
      mh.dispose();
    }
    if (o.grille) {
      const n = Math.max(2, Math.round(o.w / 0.24));
      for (let i = 1; i < n; i++) {
        const u = o.x - o.w / 2 + (o.w * i) / n;
        const g = chamferBox(0.04, o.h - 0.06, 0.04, 0.01);
        const p = f.at(u, thickness / 2 - 0.05);
        w.batch.add(g, 'metal_rusted', {
          matrix: xform([p[0], base + o.y + o.h / 2, p[1]], f.rotY), surface: 'metal', uvScale: 0.4,
        });
        g.dispose();
      }
    }
    if (o.shutter) {
      // rolled-up corrugated shutter box above a shopfront
      const g = chamferBox(o.w + 0.22, 0.35, 0.35, 0.04);
      const p = f.at(o.x, thickness / 2 + 0.05);
      w.batch.add(g, 'corrugated_metal', {
        matrix: xform([p[0], base + o.y + o.h + 0.20, p[1]], f.rotY), surface: 'metal', uvScale: 0.35,
        cast: false,
      });
      g.dispose();
    }
  }

  /* --- pilasters ----------------------------------------------------- */
  if (pilasters) {
    const xs = Array.isArray(pilasters)
      ? pilasters
      : Array.from({ length: pilasters }, (_, i) => -f.L / 2 + (f.L * (i + 0.5)) / pilasters);
    for (const u of xs) {
      const ph = (cornice ? cornice.y ?? height : height) - 0.02;
      // A pilaster is applied to the OUTSIDE of a facade. Centring it on the
      // wall pushed 0.10 m of it into the room, and the resulting inside corner
      // caught a one-pixel specular line that chromatic aberration turned into a
      // rainbow seam down `level_interior`. Offset so its back is flush.
      const proj = 0.20;
      const g = chamferBox(0.44, ph, thickness + proj, 0.04);
      const p = f.at(u, proj / 2);
      w.batch.add(g, mat, { matrix: xform([p[0], base + ph / 2, p[1]], f.rotY), surface, uvScale: 1.2 });
      g.dispose();
      const cap = chamferBox(0.58, 0.15, thickness + 0.32, 0.034);
      const pc = f.at(u, 0.06);
      w.batch.add(cap, trimMat, {
        matrix: xform([pc[0], base + ph + 0.06, pc[1]], f.rotY), surface: 'concrete', uvScale: 0.7,
      });
      cap.dispose();
    }
  }

  /* --- cornice (both faces) ------------------------------------------ */
  if (cornice) {
    const cy = cornice.y ?? height;
    const proj = cornice.proj ?? 0.24, ch = cornice.h ?? 0.30;
    for (const side of (cornice.side === 'one' ? [1] : [1, -1])) {
      const g = corniceProfile(f.L, proj, ch);
      g.rotateY(side > 0 ? -Math.PI / 2 : Math.PI / 2);
      const p = f.at(0, side * (thickness / 2));
      w.batch.add(g, cornice.mat ?? trimMat, {
        matrix: xform([p[0], base + cy - ch, p[1]], f.rotY), surface: 'concrete', uvScale: 0.9,
      });
      g.dispose();
    }
  }

  /* --- parapet + coping ---------------------------------------------- */
  if (parapet) {
    const py = parapet.y ?? height;
    const ph = parapet.h ?? 0.95;
    const pt = parapet.thickness ?? thickness * 0.8;
    /*
     * Stepped, not one bar.
     *
     * This is the top edge of the souk house — the roofline that closes the
     * right lane in `level_square`, `level_rooftop` and `wpn_rifle_hip` — and a
     * single `chamferBox(f.L, ph, pt)` draws a 28 m ruler against the sky.
     * CRITIC.md §3 calls a flat straight roofline an outright fail, and the
     * blind test found it in every one of our frames. Same triangle order of
     * magnitude, same batch group, no extra draw call.
     */
    const segs = Math.max(2, Math.min(6, Math.round(f.L / 4.2)));
    let cur = -f.L / 2;
    let prev = null;
    for (let s = 0; s < segs; s++) {
      const want = s === segs - 1 ? (f.L / 2 - cur) : (f.L / segs) * rng.range(0.6, 1.45);
      const sl = Math.min(want, f.L / 2 - cur);
      if (sl < 0.35) break;
      const uMid = cur + sl / 2;
      const uStart = cur;
      cur += sl;
      let h = ph * rng.range(0.62, 1.45);
      if (prev !== null && Math.abs(h - prev) < 0.18) h = prev + rng.sign() * rng.range(0.2, 0.5);
      h = Math.max(0.3, Math.min(ph * 2.1, h));
      const pm = f.at(uMid);
      const g = chamferBox(sl, h, pt, 0.045);
      w.batch.add(g, parapet.mat ?? mat, {
        matrix: xform([pm[0], base + py + h / 2, pm[1]], f.rotY), surface, uvScale,
      });
      g.dispose();
      if (rng() > 0.15) {
        const cap = chamferBox(sl, 0.11, pt + 0.16, 0.038);
        w.batch.add(cap, 'concrete_wall', {
          matrix: xform([pm[0], base + py + h + 0.05, pm[1]], f.rotY), surface: 'concrete', uvScale: 0.7,
        });
        cap.dispose();
      }
      if (rng() < 0.5 && sl > 1.0) {
        const pierH = h + rng.range(0.22, 0.7);
        const pw = rng.range(0.32, 0.48);
        const pp = f.at(uStart + (rng() < 0.5 ? 0.16 : sl - 0.16));
        const p = chamferBox(pw, pierH, pt + 0.1, 0.035);
        w.batch.add(p, parapet.mat ?? mat, {
          matrix: xform([pp[0], base + py + pierH / 2, pp[1]], f.rotY), surface, uvScale,
        });
        p.dispose();
        const pc = chamferBox(pw + 0.14, 0.09, pt + 0.24, 0.026);
        w.batch.add(pc, 'concrete_wall', {
          matrix: xform([pp[0], base + py + pierH + 0.045, pp[1]], f.rotY), surface: 'concrete', uvScale: 0.6,
        });
        pc.dispose();
      }
      prev = h;
    }
    if (collide) {
      const c = f.at(0);
      w.blocker(c[0], base + py + ph / 2, c[1], f.L, ph, pt, f.rotY, 'concrete', false);
    }
  }

  /* --- blown plaster revealing brick --------------------------------- */
  for (let i = 0; i < damage; i++) {
    const u = rng.range(-f.L / 2 + 0.6, f.L / 2 - 0.6);
    const dh = rng.range(0.5, 1.7);
    const dw = rng.range(0.6, 2.0);
    const y = rng.range(0.05, Math.max(0.2, height - dh - 0.4));
    // skip if it lands on an opening
    if (ops.some((o) => Math.abs(o.x - u) < o.w / 2 + dw / 2 && y < o.y + o.h && y + dh > o.y)) continue;
    const side = rng.sign();
    // sit the patch ON the face and let damagePatch place the core behind it
    const p = f.at(u, side * (thickness / 2 + 0.002));
    damagePatch(w, {
      cx: p[0], cy: base + y + dh / 2, cz: p[1], rotY: f.rotY, nrm: side,
      w: dw, h: dh, plasterMat: mat, plasterSurface: surface, rng,
    });
  }

  /* --- collision: piers + spandrel bands ------------------------------ */
  if (collide) {
    const sorted = ops.slice().sort((p, q) => p.x - q.x);
    let cursor = -f.L / 2;
    const solidStrip = (u0, u1) => {
      if (u1 - u0 < 0.02) return;
      const c = f.at((u0 + u1) / 2);
      w.blocker(c[0], base + height / 2, c[1], u1 - u0, height, thickness, f.rotY, surface);
    };
    for (const o of sorted) {
      const x0 = o.x - o.w / 2, x1 = o.x + o.w / 2;
      solidStrip(cursor, Math.max(cursor, x0));
      cursor = Math.max(cursor, x1);
      const c = f.at(o.x);
      if (o.y > 0.06) {
        w.blocker(c[0], base + o.y / 2, c[1], o.w, o.y, thickness, f.rotY, surface, false);
      }
      const top = o.y + o.h;
      if (top < height - 0.06) {
        w.blocker(c[0], base + (top + height) / 2, c[1], o.w, height - top, thickness, f.rotY, surface, false);
      }
    }
    solidStrip(cursor, f.L / 2);
  }
  return f;
}

/** A floor / roof slab with a chamfered edge, optionally with a stairwell hole. */
export function slab(w, { x0, z0, x1, z1, y, thickness = 0.32, mat = 'concrete_floor', surface = 'concrete', holes = [], uvScale = 2.2, collide = true, top = null }) {
  const pieces = [];
  const X0 = Math.min(x0, x1), X1 = Math.max(x0, x1);
  const Z0 = Math.min(z0, z1), Z1 = Math.max(z0, z1);
  if (!holes.length) {
    pieces.push([X0, Z0, X1, Z1]);
  } else {
    // guillotine split around the (few, axis-aligned) holes
    let rects = [[X0, Z0, X1, Z1]];
    for (const h of holes) {
      const next = [];
      for (const r of rects) {
        const [a, b, c, d] = r;
        if (h.x1 <= a || h.x0 >= c || h.z1 <= b || h.z0 >= d) { next.push(r); continue; }
        if (h.x0 > a) next.push([a, b, h.x0, d]);
        if (h.x1 < c) next.push([h.x1, b, c, d]);
        const mx0 = Math.max(a, h.x0), mx1 = Math.min(c, h.x1);
        if (h.z0 > b) next.push([mx0, b, mx1, h.z0]);
        if (h.z1 < d) next.push([mx0, h.z1, mx1, d]);
      }
      rects = next;
    }
    pieces.push(...rects);
  }
  for (const [a, b, c, d] of pieces) {
    const sw = c - a, sd = d - b;
    if (sw < 0.02 || sd < 0.02) continue;
    const g = chamferBox(sw, thickness, sd, 0.04);
    w.batch.add(g, mat, {
      matrix: xform([(a + c) / 2, y - thickness / 2, (b + d) / 2], 0),
      surface, uvScale,
    });
    g.dispose();
    if (top && top !== mat) {
      const t = chamferBox(sw - 0.06, 0.055, sd - 0.06, 0.024);
      w.batch.add(t, top, {
        matrix: xform([(a + c) / 2, y + 0.01, (b + d) / 2], 0), surface, uvScale: uvScale * 1.5,
      });
      t.dispose();
    }
    if (collide) {
      w.blockerBox(a, y - thickness, b, c, y, d, surface, false);
    }
    w.zone(a, b, c, d, y, top ? surface : surface);
  }
}
